import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import { DelayAreaSelector } from "@/shared/selectors/DelayAreaSelector";
import { modalStyles } from "@/shared/styles/modalStyles";
import { FooterModalButtons } from "../../../../controls/buttons/FooterModalButtons";
import { NotificationSnackBar } from "../../../../controls/snackbars/notification-snackbar";
import {
  useDeleteDelayCategoryMutation,
  useUpsertDelayCategoryMutation,
} from "../../hooks/mutations";
import {
  DELAY_CATEGORY_INITIAL_VALUES,
  DelayCategory,
} from "../models/CatalogCategory";
import ColorPicker from "./ColorPicker/ColorPicker";

type Props = {
  delayCategory: DelayCategory | null;
  modal: "create" | "update" | "delete" | "";
  onHide: () => void;
};

export const UpsertDeleteModal = ({ delayCategory, modal, onHide }: Props) => {
  const [title, setTitle] = useState<string>("");
  const [notificationMessage, setNotificationMessage] = useState("");
  const [error, setError] = useState("");

  const successfullyMessage = `The category was ${modal}d successfully`;

  const { control, reset, handleSubmit } = useForm<DelayCategory>({
    defaultValues: DELAY_CATEGORY_INITIAL_VALUES,
  });

  const upsertMutation = useUpsertDelayCategoryMutation();
  const deleteMutation = useDeleteDelayCategoryMutation();

  const isSubmitLoading = upsertMutation.isPending || deleteMutation.isPending;

  const validateNoSpecialChars = (value: string) =>
    /^[a-zA-Z0-9 _-]*$/.test(value) || "No special characters";

  const onSubmit: SubmitHandler<DelayCategory> = async (
    data: DelayCategory,
  ) => {
    if (modal !== "delete") {
      upsertMutation.mutate(data, {
        onSuccess: () => {
          setNotificationMessage(successfullyMessage);
          onHide();
        },
        onError: (err: unknown) => {
          setError(err instanceof Error ? err.message : "An error occurred");
        },
      });
    } else {
      deleteMutation.mutate(data.DelayCategoryId as number, {
        onSuccess: () => {
          setNotificationMessage(successfullyMessage);
          onHide();
        },
        onError: (err: unknown) => {
          setError(err instanceof Error ? err.message : "An error occurred");
        },
      });
    }
  };

  useEffect(() => {
    if (modal !== "") {
      reset(
        modal === "create"
          ? DELAY_CATEGORY_INITIAL_VALUES
          : delayCategory || DELAY_CATEGORY_INITIAL_VALUES,
      );
      setTitle(
        modal === "create" ? "CREATE" : modal === "update" ? "EDIT" : "DELETE",
      );
    }
  }, [modal, delayCategory]);

  return (
    <>
      <MesfModal
        id="modal-category"
        title={`${title} CATEGORY`}
        open={modal !== ""}
        handleClose={() => onHide()}
        maxWidth="sm"
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <MesfModal.Content style={{ padding: "15px 30px" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={12}>
                <Controller
                  name="DelayCategoryName"
                  control={control}
                  rules={{
                    required: "Name is required",
                    validate: validateNoSpecialChars,
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      label="Name"
                      variant="outlined"
                      value={field.value}
                      onChange={field.onChange}
                      error={!!error}
                      helperText={error?.message}
                      fullWidth
                      autoComplete="off"
                      size="small"
                      disabled={modal === "delete"}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6} md={6}>
                <Controller
                  name="AssetId"
                  control={control}
                  rules={{ required: "Delay Area is required" }}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <DelayAreaSelector
                        value={field.value}
                        onChange={field.onChange}
                        isAllOption={false}
                        disabled={modal === "delete"}
                      />
                      {error && (
                        <Box component="span" sx={modalStyles.errorLabel}>
                          {error?.message}
                        </Box>
                      )}
                    </>
                  )}
                />
              </Grid>
              <Grid item xs={3} md={3}>
                <Controller
                  name="DelayCategoryColor"
                  control={control}
                  rules={{ required: "Color is required" }}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <ColorPicker
                        onChange={(_: string, color: string) =>
                          field.onChange(color)
                        }
                        color={field.value}
                        disabled={modal === "delete"}
                        label="Color"
                      />
                      {error && (
                        <Box component="span" sx={modalStyles.errorLabel}>
                          {error?.message}
                        </Box>
                      )}
                    </>
                  )}
                />
              </Grid>
              <Grid item xs={3} md={3} style={{ marginTop: -10 }}>
                <Controller
                  name="IsActive"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      sx={modalStyles.checkbox}
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={field.onChange}
                          name="IsActive"
                          color="primary"
                        />
                      }
                      label="Is Active"
                      disabled={modal === "delete"}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </MesfModal.Content>
          <MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
            <FooterModalButtons
              isSubmitLoading={isSubmitLoading}
              onHide={() => onHide()}
              buttonLabel={modal === "delete" ? "Delete" : "Save"}
              buttonColor={modal === "delete" ? "secondary" : "primary"}
            />
          </MesfModal.Actions>
        </form>
      </MesfModal>
      <NotificationSnackBar
        message={notificationMessage}
        onHide={() => setNotificationMessage("")}
      />
      <ErrorModal error={error} onHide={() => setError("")} />
    </>
  );
};
